import PropTypes from 'prop-types';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { useStoreId } from '@thd-nucleus/experience-context';
import { useDataModel } from '@thd-nucleus/data-sources';
import { packagesModel } from '../../models/packagesModel';
import { generateTags } from '../../utils/generate-tags';
import { OPEN_GRAPH_FACEBOOK_ADMIN, APPENDED_BRAND_NAME } from '../../constants';
import { getSeoURL, getMetaDescription } from './packages-helpers';

// --- Tag Handler ---

/**
 * Generates metadata tags for packages pages.
 * @param {Object} params - Handler parameters
 * @param {Object} params.data - Data from useDataModel
 * @param {Object} params.props - Component props
 * @returns {Array} Array of metadata tags
 */
const packagesMetadataHandler = ({ data, props }) => {
  const {
    productCategory, seo1, seo2, slug, packageId
  } = props;

  // Extract packages metadata from data and normalize fields
  const metadata = data?.packages?.[0]?.metadata || {};

  const packageName = metadata.name || '';
  const packageTitle = metadata.title || '';

  // Process package image (replace <SIZE> placeholder with default 400)
  const packageImage = metadata.image?.url?.replace(/(<SIZE?>)/i, '400') || '';
  const seoTitle = packageName ? `${packageName}${APPENDED_BRAND_NAME}` : '';
  const metaDescription = getMetaDescription(packageName, packageTitle) || '';
  const seoUrl = getSeoURL(productCategory, seo1, seo2, slug, packageId);

  return generateTags({
    title: seoTitle,
    description: metaDescription,
    ogImage: packageImage,
    ogTitle: seoTitle,
    ogUrl: seoUrl,
    ogDescription: metaDescription,
    ogType: 'Bundles and Sets',
    ogSiteName: 'The Home Depot',
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    canonical: seoUrl
  });
};

const PackagesMetadata = ({ params }) => {
  // /p/sets/:seo1?/:seo2?/:slug?/:packageId
  const { packageId } = params;

  const storeId = useStoreId();
  const packageOptions = {
    variables: {
      storeId,
      packageId
    }
  };
  const { data } = useDataModel('packages', packageOptions);

  useHelmet('packagesMetadata', { data, props: params }, packagesMetadataHandler, [packageId, storeId, data]);
  return null;
};

PackagesMetadata.displayName = 'PackagesMetadata';

PackagesMetadata.dataModel = packagesModel;

PackagesMetadata.propTypes = {
  params: PropTypes.shape({
    packageId: PropTypes.string,
    productCategory: PropTypes.string,
    seo1: PropTypes.string,
    seo2: PropTypes.string,
    slug: PropTypes.string
  })
};

PackagesMetadata.defaultProps = {
  params: {}
};

export { PackagesMetadata, packagesMetadataHandler };
